import type { Node, Edge } from '../../../core/models/scene';
import type {
  ScenarioDelta,
  NodeAddition,
  EdgeAddition,
} from '../../../core/data-profiles/scenarios/ScenarioDelta';
import { buildStatusIndex } from '../../../core/data-profiles/scenarios/ScenarioModel';
import { getShapeManifest } from '../../../lib/diagram/registries';

/**
 * Network scenario projector — applies a ScenarioDelta to the current
 * network scene and returns the projected nodes and edges.
 *
 * This mirrors `processScenarioProjector.ts` but keeps network-specific
 * quirks in one place:
 *
 *   - `additionToNode` reads dimensions + defaults from the AWS / basic
 *     network shape manifest registry so ghost nodes match real shape
 *     sizes and pick up the service colour / icon URL automatically.
 *
 *   - Update change application supports the `scenarioStatus` attr
 *     badge (`added` / `removed` / `changed`) so the canvas renderer
 *     can tint ghost nodes without extra stores. The NetworkResourcePanel
 *     reads this attr and hides the edit buttons when a node is part
 *     of an unapplied scenario.
 *
 *   - Edge type mapping uses the 6 network connector kinds (containment,
 *     sg_access, data_flow, role_assumption, routing, governance) so
 *     diff edges render with the right arrow head and stroke colour.
 *
 * Like `processScenarioProjector`, this function is a pure projection:
 * it produces a new `{ nodes, edges }` tuple and never mutates the base
 * scene or the ScenariosStore. Scenario commit / discard is handled by
 * `scenariosStore` upstream.
 */

const NETWORK_CATEGORY = 'network';

const resolveDefaultDimensions = (shapeType: string): { width: number; height: number } => {
  const manifest = getShapeManifest('network', shapeType);
  return manifest?.defaultDimensions || { width: 180, height: 60 };
};

const resolveDefaultProps = (shapeType: string): Record<string, unknown> => {
  const manifest = getShapeManifest('network', shapeType);
  if (!manifest?.defaultProps) return {};
  try {
    return typeof manifest.defaultProps === 'function'
      ? (manifest.defaultProps as () => Record<string, unknown>)()
      : (manifest.defaultProps as Record<string, unknown>);
  } catch {
    return {};
  }
};

const additionToNode = (addition: NodeAddition): Node => {
  const dims = resolveDefaultDimensions(addition.type);
  const defaults = resolveDefaultProps(addition.type);
  const label = addition.name || addition.title || 'Resource';
  const attrs: Record<string, unknown> = {
    ...defaults,
    ...(addition.props ?? {}),
    name: label,
    scenarioStatus: 'added',
  };

  return {
    id: addition.id as string,
    type: addition.type,
    category: NETWORK_CATEGORY,
    x: addition.position?.x ?? 0,
    y: addition.position?.y ?? 0,
    w: dims.width,
    h: dims.height,
    width: dims.width,
    height: dims.height,
    text: label,
    attrs,
    props: {
      ...defaults,
      ...(addition.props ?? {}),
    },
  } as Node;
};

const NETWORK_EDGE_TYPE_MAP: Record<string, string> = {
  containment: 'containment',
  sg_access: 'sg_access',
  data_flow: 'data_flow',
  identity: 'role_assumption',
  routing: 'routing',
  governance: 'governance',
};

const additionToEdge = (addition: EdgeAddition): Edge => {
  const mapped = NETWORK_EDGE_TYPE_MAP[addition.type] ?? addition.type;

  return {
    id: addition.id as string,
    type: mapped as Edge['type'],
    from: addition.from as string,
    to: addition.to as string,
    attrs: {
      infraEdgeKind: mapped,
      scenarioStatus: 'added',
      ...(addition.props ?? {}),
    },
  } as Edge;
};

/**
 * Apply `delta` to `baseNodes` + `baseEdges`. Returns the projected
 * scene and a status index used by the ScenariosSubPanel to count
 * adds/removes/updates per lifecycle bucket.
 */
export const projectNetworkScenario = (
  baseNodes: Node[],
  baseEdges: Edge[],
  delta: ScenarioDelta<any, any>,
): { nodes: Node[]; edges: Edge[]; statusIndex: ReturnType<typeof buildStatusIndex> } => {
  if (
    delta.additions.length === 0 &&
    delta.removals.length === 0 &&
    delta.moves.length === 0 &&
    delta.updates.length === 0 &&
    delta.edgeAdditions.length === 0 &&
    delta.edgeRemovals.length === 0
  ) {
    return {
      nodes: baseNodes,
      edges: baseEdges,
      statusIndex: buildStatusIndex(delta),
    };
  }

  const removalIds = new Set(delta.removals.map((r) => r.nodeId as string));
  const edgeRemovalIds = new Set(delta.edgeRemovals.map((r) => r.edgeId as string));
  const updateIndex = new Map(delta.updates.map((u) => [u.nodeId as string, u]));

  const projectedNodes: Node[] = baseNodes
    .map((node) => {
      // Mark removed nodes as ghost-removed but keep them visible so the
      // user can see what's going away. The canvas renderer reads
      // attrs.scenarioStatus to tint them red.
      if (removalIds.has(node.id)) {
        return {
          ...node,
          attrs: {
            ...(node.attrs as Record<string, unknown>),
            scenarioStatus: 'removed',
          },
        } as Node;
      }

      const update = updateIndex.get(node.id);
      if (!update) return node;

      const { field, newValue } = update.change as { field: string; newValue: unknown };
      return {
        ...node,
        [field]: newValue,
        attrs: {
          ...(node.attrs as Record<string, unknown>),
          [field]: newValue,
          scenarioStatus: 'changed',
        },
        props: {
          ...(node.props as Record<string, unknown>),
          [field]: newValue,
        },
      } as Node;
    })
    .concat(delta.additions.map(additionToNode));

  const projectedEdges: Edge[] = baseEdges
    .map((edge) => {
      if (edgeRemovalIds.has(edge.id) || removalIds.has(edge.from) || removalIds.has(edge.to)) {
        return {
          ...edge,
          attrs: {
            ...(edge.attrs as Record<string, unknown>),
            scenarioStatus: 'removed',
          },
        } as Edge;
      }
      return edge;
    })
    .concat(delta.edgeAdditions.map(additionToEdge));

  return {
    nodes: projectedNodes,
    edges: projectedEdges,
    statusIndex: buildStatusIndex(delta),
  };
};
